import { Head, Link, router, usePage } from "@inertiajs/react"
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { ChangeFeed, type CompetitorChangeItem } from "./ChangeFeed"
import type { CompetitorItem } from "./Index"
import type { PageProps } from "@/types/inertia"

type CompetitorPageItem = {
  id: number
  url: string
  title: string | null
  headings: string[]
  word_count: number
  status: string
  first_seen_at: string | null
  last_changed_at: string | null
}

type SnapshotPoint = { date: string; page_count: number; changes_count: number }

type Props = PageProps & {
  competitor: CompetitorItem
  pages: CompetitorPageItem[]
  changes: CompetitorChangeItem[]
  snapshots: SnapshotPoint[]
}

const TEAL = "#1B4A52"

function formatDate(iso: string | null) {
  if (!iso) return "—"
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
}

function SiteSizeChart({ snapshots }: { snapshots: SnapshotPoint[] }) {
  if (snapshots.length < 2) {
    return (
      <p className="py-8 text-center text-sm text-ink-muted">
        Site-size trend appears after a few scans.
      </p>
    )
  }
  return (
    <ResponsiveContainer width="100%" height={160}>
      <AreaChart data={snapshots} margin={{ top: 4, right: 0, left: -25, bottom: 0 }}>
        <defs>
          <linearGradient id="gradPages" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={TEAL} stopOpacity={0.3} />
            <stop offset="95%" stopColor={TEAL} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-hairline)" vertical={false} />
        <XAxis
          dataKey="date"
          tick={{ fill: "var(--color-ink-muted)", fontSize: 11 }}
          axisLine={false}
          tickLine={false}
          interval="preserveStartEnd"
          tickFormatter={(d: string) =>
            new Date(d + "T00:00:00Z").toLocaleDateString("en-GB", { month: "short", day: "numeric", timeZone: "UTC" })
          }
        />
        <YAxis tick={{ fill: "var(--color-ink-muted)", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
        <Tooltip formatter={(value) => [value, "Pages"]} />
        <Area type="stepAfter" dataKey="page_count" name="Pages" stroke={TEAL} strokeWidth={2} fill="url(#gradPages)" dot={false} />
      </AreaChart>
    </ResponsiveContainer>
  )
}

export default function CompetitorShow() {
  const { props } = usePage<Props>()
  const { competitor, pages, changes, snapshots } = props
  const flash = props.flash

  return (
    <AdminShell>
      <Head title={`Competitor — ${competitor.name}`}>
        <meta name="description" content={`Tracked changes on ${competitor.name}'s website.`} />
        <meta property="og:title" content={`Competitor — ${competitor.name}`} />
        <meta property="og:description" content={`Tracked changes on ${competitor.name}'s website.`} />
      </Head>

      <PageHeader title={competitor.name} description={`What's changing on ${competitor.host ?? competitor.url}.`} />

      {flash?.notice && <p className="mt-4 text-sm text-accent">{flash.notice}</p>}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <a href={competitor.url} target="_blank" rel="noreferrer noopener" className="font-mono text-sm">
          {competitor.url}
        </a>
        {competitor.brand && <Badge tone="accent">{competitor.brand === "gisela" ? "Gisela" : "Sanctuary"}</Badge>}
        {competitor.last_status === "error" && <Badge tone="signal">last scan failed</Badge>}
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => router.post(`/admin/competitors/${competitor.id}/check`)}>
            Check now
          </Button>
          <Link href="/admin/competitors" className="no-underline">
            <Button size="sm" variant="ghost">All competitors</Button>
          </Link>
        </div>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <div className="rounded-lg border border-hairline bg-surface p-5">
          <p className="text-sm text-ink-muted">Pages tracked</p>
          <p className="mt-1 text-3xl font-light text-ink-display">{competitor.page_count}</p>
          <p className="mt-1 text-xs text-ink-muted">last checked {formatDate(competitor.last_checked_at)}</p>
        </div>
        <div className="rounded-lg border border-hairline bg-surface p-5 lg:col-span-2">
          <p className="mb-2 text-sm text-ink-muted">Site size over time (public pages found per scan)</p>
          <SiteSizeChart snapshots={snapshots} />
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-hairline bg-surface">
          <div className="border-b border-hairline px-5 py-3">
            <h2>Change history</h2>
          </div>
          <div className="max-h-[560px] overflow-y-auto">
            <ChangeFeed changes={changes} emptyText="No changes detected yet." />
          </div>
        </div>

        <div className="rounded-lg border border-hairline bg-surface">
          <div className="border-b border-hairline px-5 py-3">
            <h2>Their pages</h2>
          </div>
          <div className="max-h-[560px] overflow-y-auto">
            {pages.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-ink-muted">
                No pages captured yet — the first scan may still be running.
              </p>
            ) : (
              <ul className="divide-y divide-hairline">
                {pages.map((p) => (
                  <li key={p.id} className="px-5 py-3">
                    <div className="flex items-baseline justify-between gap-3">
                      <a href={p.url} target="_blank" rel="noreferrer noopener" className="min-w-0 truncate text-sm">
                        {p.title || p.url}
                      </a>
                      <span className="shrink-0 font-mono text-xs text-ink-muted">{p.word_count.toLocaleString()} words</span>
                    </div>
                    <p className="mt-0.5 truncate font-mono text-xs text-ink-muted">
                      {p.url.replace(/^https?:\/\/[^/]+/, "") || "/"}
                      {p.status === "gone" && <Badge tone="signal" className="ml-2">gone</Badge>}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </AdminShell>
  )
}
